import React, { useState, useEffect } from 'react';
import { connect } from 'react-redux';
import styled from 'styled-components';
import { getTranslatedString } from 'helpers/i18n';
import { formatPhone } from 'helpers/Formatter';

import { PageContainer, PageHeaderTitle, PageHeaderDetails } from '../common/Page';

import ApplicantCard from 'components/ui/ApplicantCard/ApplicantCard';
import PillButton from 'components/ui/Buttons/PillButton';
import TextButton from 'components/ui/Buttons/TextButton';
import { media } from 'helpers/MediaHelper';

import ConfirmationDialog from 'components/ui/Dialogs/ConfirmationDialog';

import { storeSelectedApplicants, cancelRenewal } from '../../store/actions';

const CANCEL_RENEWAL_STATES = {
  confirm: 'confirm',
  error: 'error',
  success: 'success',
  loading: 'loading',
};

const StyledApplicantCardContainer = styled.div`
  margin-bottom: 24px;
`;

const StyledFooter = styled.div`
  display: flex;
  justify-content: space-between;
  margin-top: 15px;
  ${media.mobile`
    flex-direction: column-reverse;
    height: 100px;
  `};
`;

const AssignLeaseHolders = ({
  closeWizard,
  hideWizardControls,
  pageIndex,
  setPageIndex,
  lastPageIndex,
  cotenants,
  storeSelectedApplicants,
  renewal,
  cancelRenewal,
}) => {
  const [applicantList, setApplicantList] = useState([]);
  const [showCancelDialog, setShowCancelDialog] = useState(false);
  const [cancelDialogState, setCancelDialogState] = useState(CANCEL_RENEWAL_STATES.confirm);

  useEffect(
    () => {
      hideWizardControls();
      if (cotenants) {
        let applicantList = cotenants.map(cotenant => ({
          ...cotenant,
          isSelected: false,
        }));

        setApplicantList(applicantList);
      }
    },
    [cotenants, hideWizardControls]
  );

  const handleRenewalCreation = () => {
    const selectedApplicants = applicantList.filter(applicant => applicant.isSelected);
    storeSelectedApplicants(selectedApplicants);
    if (pageIndex !== lastPageIndex) {
      return setPageIndex(pageIndex + 1);
    }
  };

  const handleApplicantSelect = (value, index) => {
    let selectedApplicant = [...applicantList];
    selectedApplicant[index].isSelected = value;

    setApplicantList(selectedApplicant);
  };

  const handleCancelRenewal = async () => {
    setCancelDialogState(CANCEL_RENEWAL_STATES.loading);
    try {
      await cancelRenewal(renewal.id);
      setCancelDialogState(CANCEL_RENEWAL_STATES.success);
    } catch (err) {
      setCancelDialogState(CANCEL_RENEWAL_STATES.error);
    }
  };

  const openCancelDialog = () => {
    setCancelDialogState(CANCEL_RENEWAL_STATES.confirm);
    setShowCancelDialog(true);
  };

  const closeCancelDialog = () => {
    setShowCancelDialog(false);
  };

  const closeWizardOnCancelSuccess = () => {
    closeCancelDialog();
    closeWizard();
  };

  return (
    <PageContainer>
      <PageHeaderTitle>
        {getTranslatedString('landlordPortal.rentRoll.renewals.assignLeaseHolders.title')}
      </PageHeaderTitle>
      <PageHeaderDetails>
        {getTranslatedString(
          'landlordPortal.rentRoll.renewals.assignLeaseHolders.applicantsInstructions'
        )}
      </PageHeaderDetails>
      <PageHeaderDetails>
        {getTranslatedString(
          'landlordPortal.rentRoll.renewals.assignLeaseHolders.noApplicantsInstructions'
        )}
      </PageHeaderDetails>
      <PageHeaderDetails>
        {getTranslatedString(
          'landlordPortal.rentRoll.renewals.assignLeaseHolders.cancellationInfo'
        )}
      </PageHeaderDetails>
      <div style={{ marginTop: '24px' }}>
        {applicantList.map((applicant, index) => {
          return (
            <StyledApplicantCardContainer>
              <ApplicantCard
                fullName={`${applicant.first_name} ${applicant.last_name}`}
                email={applicant.email}
                phoneNumber={formatPhone(applicant.phone_number)}
                isSelectable={true}
                isChecked={applicant.isSelected}
                handleSelect={value => handleApplicantSelect(value, index)}
              />
            </StyledApplicantCardContainer>
          );
        })}
      </div>
      <StyledFooter>
        <TextButton onClick={openCancelDialog}>
          {getTranslatedString('landlordPortal.rentRoll.renewals.cancelRenewal')}
        </TextButton>
        <PillButton onClick={handleRenewalCreation} type="button">
          {getTranslatedString('continue')}
        </PillButton>
      </StyledFooter>

      {/* Dialog when cancelling renewal */}
      <ConfirmationDialog
        showConfirmationDialog={showCancelDialog}
        dialogState={cancelDialogState}
        confirm={{
          primaryBtnText: getTranslatedString(
            'landlordPortal.rentRoll.renewals.cancelConfirmation.confirm'
          ),
          headerText: getTranslatedString(
            'landlordPortal.rentRoll.renewals.cancelConfirmation.header'
          ),
          bodyText: getTranslatedString('landlordPortal.rentRoll.renewals.cancelConfirmation.body'),
          primaryAction: handleCancelRenewal,
          secondaryBtnText: getTranslatedString('close'),
          secondaryAction: closeCancelDialog,
        }}
        success={{
          primaryBtnText: getTranslatedString('landlordPortal.rentRoll.renewals.successButton'),
          headerText: getTranslatedString('landlordPortal.rentRoll.renewals.cancelRenewalSuccess'),
          bodyText: getTranslatedString(
            'landlordPortal.rentRoll.renewals.cancelRenewalSuccessBody'
          ),
          action: closeWizardOnCancelSuccess,
        }}
        loading={{
          headerText: getTranslatedString('landlordPortal.rentRoll.renewals.loadingHeader'),
          bodyText: getTranslatedString('landlordPortal.rentRoll.renewals.doNotReload'),
        }}
        error={{
          headerText: getTranslatedString('landlordPortal.rentRoll.renewals.cancelError'),
          bodyText: getTranslatedString('landlordPortal.rentRoll.renewals.cancelErrorBody'),
          primaryBtnText: getTranslatedString(
            'landlordPortal.rentRoll.renewals.cancelErrorTryAgain'
          ),
          action: closeCancelDialog,
        }}
      />
    </PageContainer>
  );
};

const mapStateToProps = store => ({
  cotenants: store.landlord.renewals.leaseCotenants,
  leaseId: store.landlord.renewals.leaseId,
  selectedProposal: store.landlord.renewals.selectedProposal,
  selectedLeaseTerm: store.landlord.renewals.selectedLeaseTerm,
  details: store.landlord.renewals.details,
  renewal: store.landlord.renewals.renewal,
});

const mapDispatchToProps = {
  storeSelectedApplicants,
  cancelRenewal,
};

export default connect(mapStateToProps, mapDispatchToProps)(AssignLeaseHolders);
